import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { MapPin, BookOpen, Truck, Search } from '../../lib/lucide-proxy';
import Button from '../ui/Button';

const Hero = () => {
  const navigate = useNavigate();

  return (
    <section className="relative bg-cream-300 overflow-hidden min-h-[85vh] flex items-center">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-0 right-0 w-2/3 h-full bg-primary-200/30 skew-x-12 transform origin-top-right" />
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-copper-200/30 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-10 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="inline-flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-sm mb-6 border border-gray-100">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium text-dark-600 tracking-wide uppercase">Conexão Local</span>
            </div>
            
            <h1 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl text-dark-900 leading-[1.1] mb-8">
              Sua Cervejaria Favorita,<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-copper-600">
                Ao Seu Lado
              </span>
            </h1>

            <p className="text-xl text-dark-600 leading-relaxed mb-10 max-w-lg">
              A plataforma completa para conectar você aos produtores locais. Descubra as melhores cervejarias artesanais da sua região e valorize o comércio local.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                variant="primary" 
                size="lg" 
                onClick={() => navigate('/cervejarias')}
                className="shadow-lg shadow-primary-500/20 px-8"
              >
                <div className="flex items-center">
                  <Search className="w-5 h-5 mr-2" />
                  <span>Encontrar Cervejarias</span>
                </div>
              </Button>
              
              <Button 
                variant="secondary" 
                size="lg" 
                onClick={() => navigate('/como-funciona')}
                className="bg-white"
              >
                <div className="flex items-center">
                  <BookOpen className="w-5 h-5 mr-2 text-dark-500" />
                  <span>Como Funciona</span>
                </div>
              </Button>
            </div>

            <div className="mt-12 flex items-center space-x-8 text-dark-500">
              <div className="flex flex-col">
                <span className="font-bold text-3xl text-dark-900">Local</span>
                <span className="text-sm">Apoie o pequeno</span>
              </div>
              <div className="w-px h-10 bg-dark-200" />
              <div className="flex flex-col">
                <span className="font-bold text-3xl text-dark-900">Artesanal</span>
                <span className="text-sm">Qualidade garantida</span>
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="relative hidden lg:block"
          >
            <div className="relative z-10 rounded-[2rem] overflow-hidden shadow-2xl transform rotate-2 hover:rotate-0 transition-all duration-700">
              <img 
                src="https://images.unsplash.com/photo-1555658636-326bc56f0f94?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" 
                alt="Cervejaria Artesanal" 
                className="w-full h-auto object-cover" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-8 left-8 text-white">
                <p className="font-display font-bold text-2xl">Experiência Real</p>
                <p className="opacity-90">Direto da fonte para você</p>
              </div>
            </div>

            {/* Floating Card */}
            <motion.div 
              animate={{ y: [0, -15, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-10 -left-10 bg-white p-6 rounded-2xl shadow-xl max-w-xs z-20"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center text-2xl">
                  📍
                </div>
                <div>
                  <p className="font-bold text-dark-900">Pertinho de você</p>
                  <p className="text-sm text-dark-500">Encontre as melhores opções</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;